import type { FormikValues } from 'formik';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { array, boolean, object, string, type Schema } from 'yup';

import type { DialogChildrenProps } from '@components/Dialog';
import FormDialog from '@components/Dialog/FormDialog';

import { useUpsertAlertSetting } from '@hooks/api/alerts/alert_setting';
import useReferenceRange from '@hooks/useReferenceRange';
import { useSession } from '@hooks/useSession';

import type { RefRangeOption, UserOption } from '@models/alert';

import { useListAlertRecipientSettings } from 'generated/alert_recipient_setting.generated';
import {
  AlertRecipientType,
  ReferenceRangeRiskLevel,
  type AlertSettingModel,
} from 'generated/types';

import Form from './AlertSettingForm';

const messages = defineMessages({
  required: {
    id: 'GLOBAL.required',
    defaultMessage: 'This field is required',
  },
  minRecipient: {
    id: 'alertSettingModalContent.minRecipient',
    defaultMessage: 'There must be at least one recipient',
  },
  maxRecipient: {
    id: 'alertSettingModalContent.maxRecipient',
    defaultMessage: 'A maximum of {number} recipients can be added',
  },
  range: {
    id: 'alertSettingModalContent.oneRange',
    defaultMessage: 'There must be at least one reference range',
  },
});

export type FormValues = {
  metric: {
    reference_range_id: string;
    category: string;
    code: string;
  };
  reference_ranges: RefRangeOption[];
  recipients?: UserOption[];
  notification_enabled: boolean;
  has_org_recipient: boolean;
};

interface AlertSettingDialogListenerProps {
  children: (props: DialogChildrenProps) => React.ReactNode;
  data?: AlertSettingModel;
}

export default function AlertSettingDialogListener({
  children,
  data,
}: AlertSettingDialogListenerProps) {
  const { formatMessage } = useIntl();
  const [open, setOpen] = useState(false);
  const { displayFriendlyRange } = useReferenceRange();
  const {
    state: { activeProfile },
  } = useSession();
  const [vitall_patient_id, account_id = ''] = [
    activeProfile?.vitall_patient_id ?? '',
    activeProfile?.account_id,
  ];
  const { data: accountRecipients, loading: accountRecipientsLoading } =
    useListAlertRecipientSettings({
      variables: {
        input: {
          account_id,
          recipient_type: AlertRecipientType.account,
        },
      },
    });
  const [updateAlertSetting, updateAlertSettingLoading] =
    useUpsertAlertSetting();

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const loading = accountRecipientsLoading || updateAlertSettingLoading;

  const initialValues: FormValues = {
    has_org_recipient:
      (accountRecipients?.listAlertRecipientSettings.length ?? 0) > 0,
    metric: {
      reference_range_id: data?.reference_range_id ?? '',
      category: data?.category ?? '',
      code: data?.name ?? '',
    },
    reference_ranges:
      data?.alert_parameters.map(
        ({
          alert_parameter_id,
          code,
          is_custom,
          lower_bound,
          risk,
          upper_bound,
        }) => {
          const friendly_label = displayFriendlyRange({
            code: data.name,
            parameters: [
              {
                code: data.name,
                lower_bound,
                parameter_id: alert_parameter_id,
                upper_bound,
              },
            ],
            is_custom,
          });
          return {
            id: alert_parameter_id,
            risk: risk as ReferenceRangeRiskLevel,
            is_custom,
            range_code: code,
            friendly_label,
          };
        }
      ) ?? [],
    notification_enabled:
      data?.alert_parameters.every(
        ({ notification_enabled }) => notification_enabled
      ) ?? true,
    recipients:
      data?.recipients.map(({ account_id, name, user_id }) => ({
        user_id,
        account_id,
        name,
      })) ?? [],
  };

  const validationSchema: Schema<FormValues> = object().shape({
    has_org_recipient: boolean().required(),
    metric: object().shape({
      reference_range_id: string().required(formatMessage(messages.required)),
      category: string().required(formatMessage(messages.required)),
      code: string().required(formatMessage(messages.required)),
    }),
    reference_ranges: array()
      .of(
        object().shape({
          id: string().required(formatMessage(messages.required)),
          range_code: string(),
          risk: string()
            .oneOf(Object.values(ReferenceRangeRiskLevel))
            .required(formatMessage(messages.required)),
          friendly_label: string().required(formatMessage(messages.required)),
        })
      )
      .min(1, formatMessage(messages.range))
      .required(formatMessage(messages.required)),
    recipients: array()
      .of(
        object().shape({
          account_id: string().nullable(),
          user_id: string().nullable(),
          name: string().required(formatMessage(messages.required)),
        })
      )
      // Has org recipient: Can optionally add more user recipients
      // No org recipient: Required at least 1 user recipient
      .when('has_org_recipient', ([hasOrgRecipient], schema) =>
        hasOrgRecipient
          ? schema.nullable()
          : schema
              .min(1, formatMessage(messages.minRecipient))
              .max(5, formatMessage(messages.maxRecipient, { number: 5 }))
              .required(formatMessage(messages.required))
      ),
    notification_enabled: boolean()
      .oneOf([true, false])
      .required(formatMessage(messages.required)),
  });

  const handleSubmit = async (values: FormValues) => {
    await updateAlertSetting({
      vitall_patient_id,
      account_id,
      changes: {
        recipients: (values.recipients ?? []).map(
          ({ account_id, user_id }) => ({
            user_id,
            account_id,
          })
        ),
        parameters: values.reference_ranges.map(({ id }) => ({
          notification_enabled: values.notification_enabled,
          reference_range_condition_id: id,
        })),
      },
      ...(data?.alert_setting_id
        ? {
            alert_setting_id: data.alert_setting_id,
          }
        : {
            reference_range_id: values.metric.reference_range_id,
          }),
    });
  };

  const disableSubmitBtn = (values: FormikValues) => {
    // Reference ranges
    const rangeMap = (values.reference_ranges as RefRangeOption[]).reduce(
      (acc, curr) => acc.set(curr.id, curr.friendly_label),
      new Map()
    );
    const sameRanges =
      rangeMap.size === initialValues.reference_ranges.length
        ? initialValues.reference_ranges.every(({ id }) => rangeMap.has(id))
        : false;

    // Recipients
    const recipientMap = (values.recipients as UserOption[]).reduce(
      (acc, curr) => acc.set(curr.user_id, curr.name),
      new Map()
    );
    const initialRecipients = initialValues.recipients ?? [];
    const sameRecipients =
      recipientMap.size === initialRecipients.length
        ? initialRecipients.every(({ user_id }) => recipientMap.has(user_id))
        : false;

    if (values.has_org_recipient) {
      return sameRanges;
    }
    // Check for same recipients and reference ranges
    if (sameRanges && sameRecipients) {
      return true;
    }
    return false;
  };

  return (
    <>
      {children({
        handleOpen,
        handleClose,
      })}
      <FormDialog
        content={<Form isEditing={Boolean(data)} />}
        dialogProps={{
          fullWidth: true,
        }}
        disableSubmit={disableSubmitBtn}
        initialValues={initialValues}
        loading={loading}
        onClose={handleClose}
        onSubmit={handleSubmit}
        open={open}
        persistent
        validationSchema={validationSchema}
      />
    </>
  );
}
